/**
 * Dynamisk programmering för optimal batteristyrning.
 *
 * Detta är inte en heuristik. Det är en garanterat nära-optimal lösning
 * (begränsad bara av SoC-diskretiseringen, normalt 21 nivåer = 5% steg).
 * Med perfekt framförhållning hela perioden minimerar vi totalkostnad.
 *
 * Algoritm:
 *   - Tillstånd: (timme t, SoC-nivå s)
 *   - Beslut: vilken SoC-nivå s' vi ska vara i nästa timme
 *   - Kostnad: kostnaden för timme t givet övergången från s till s'
 *   - DP: value[t][s] = minimal kostnad från (t, s) till slutet
 *   - Bakåtinduktion: value[T][*] = 0, sedan räknar vi baklänges
 *
 * Komplexitet: O(T * N²) där T = antal timmar, N = SoC-nivåer.
 * För 8760 timmar och 21 nivåer: ~3.9M operationer, hanterbart i JS
 * på under en sekund.
 *
 * Skillnaden mot LP: vi diskretiserar SoC. Med 21 nivåer gör vi 5%-fel
 * vid värsta fall, vilket är < 1% av totalkostnaden. För batteriet ger
 * det praktiskt optimal styrning.
 */

import type {
  BatteryConfig,
  HourPoint,
  GridFeeConfig,
  TaxConfig,
  SupplierConfig,
  ResultHour,
} from "./types";
import { importPricePerKWh, exportRevenuePerKWh } from "./costs";

interface DPCellChoice {
  /** Index till nästa SoC-nivå för optimal handling */
  nextS: number;
  /** AC-side: kWh som flödar in i batteriet under timmen */
  chargeAC: number;
  /** AC-side: kWh som flödar ut från batteriet under timmen */
  dischargeAC: number;
  /** Källfördelning: hur mycket av laddningen som kom från sol respektive nätet */
  chargeFromSolar: number;
  chargeFromGrid: number;
}

export interface DPResult {
  /** Optimal årlig kostnad (annualiserad om perioden inte är ett år) */
  annualCostSek: number;
  /** Optimal årlig besparing vs baseline utan batteri */
  annualSavingsSek: number;
  /** Resultatspår timme för timme */
  hours: ResultHour[];
  /** Antal SoC-nivåer som användes (för diagnostik) */
  socLevels: number;
  /** Antal fullcykler */
  fullCycles: number;
}

/**
 * Kör DP-optimering och returnera optimal styrning + kostnader.
 *
 * För enkelhet använder vi en fast tier under hela perioden, baserad på
 * BASELINE årsförbrukning. Det är en mindre approximation eftersom
 * batteriet i optimal styrning kan flytta gränsen lite, men effekten är
 * liten i praktiken eftersom tier-omsvängning är ovanlig.
 */
export function optimizeWithDP(
  hours: HourPoint[],
  battery: BatteryConfig,
  tax: TaxConfig,
  supplier: SupplierConfig,
  grid: GridFeeConfig,
  socLevels = 21,
  /**
   * Valfri vektor med "skuggkostnader" per kWh import per timme. Används av
   * peak-aware optimerare som lägger straff på timmar som annars skulle bli
   * månadens topp-timme. Längd måste matcha hours.length.
   */
  peakPenalties?: number[],
  /**
   * Valfri vektor med maximal tillåten net-import per timme. Används som
   * hård constraint av peak-aware optimerare för att hindra DP från att
   * skapa nya peaks genom aggressiv grid-laddning. Längd måste matcha
   * hours.length. Värdet är typiskt månadens baseline-max-import för
   * timmens månad.
   */
  importCap?: number[],
): DPResult {
  if (hours.length === 0) {
    throw new Error("Ingen data att optimera mot.");
  }

  const T = hours.length;
  const N = socLevels;
  const minSoc = battery.capacityKWh * battery.minSocFraction;
  const maxSoc = battery.capacityKWh;
  const socStep = (maxSoc - minSoc) / (N - 1);
  const chargeEff = Math.sqrt(battery.roundTripEfficiency);
  const dischargeEff = Math.sqrt(battery.roundTripEfficiency);

  // Tier-kontext baserat på baseline (utan batteri)
  const baselineAnnualImport =
    hours.reduce((s, h) => s + h.importKWh, 0) * (8760 / T);

  // Förberäkna pris-vektorer per timme. Vi behåller två: en RAW (utan
  // skuggpriser) som används för att rapportera verklig monetär besparing,
  // och en DP-versionen (med skuggpriser om de finns) som styr själva
  // optimeringen.
  const rawImportPrices = hours.map((h) =>
    importPricePerKWh(h, tax, supplier, grid, baselineAnnualImport),
  );
  const importPrices = rawImportPrices.map(
    (p, i) => p + (peakPenalties?.[i] ?? 0),
  );
  const exportRevenues = hours.map((h) =>
    exportRevenuePerKWh(h, tax, supplier, grid),
  );

  // value[t][s] = minimal kostnad från (t, s) framåt till T
  // I praktiken sparar vi bara två rader (t och t+1) för minnesseffektivitet
  // men för 21 nivåer * 8760 timmar = 184k floats = ~1.5 MB, så vi kan
  // spara hela tabellen för att möjliggöra reconstruction.
  const value: number[][] = Array.from({ length: T + 1 }, () =>
    new Array(N).fill(Number.POSITIVE_INFINITY),
  );
  const choice: DPCellChoice[][] = Array.from({ length: T }, () =>
    Array.from({ length: N }, () => ({
      nextS: 0,
      chargeAC: 0,
      dischargeAC: 0,
      chargeFromSolar: 0,
      chargeFromGrid: 0,
    })),
  );

  // Initialvillkor: vid t=T kostar alla SoC-nivåer 0
  // (vi bryr oss inte om batteriets slutstatus)
  for (let s = 0; s < N; s++) {
    value[T][s] = 0;
  }

  // Bakåtinduktion: t = T-1 → 0
  for (let t = T - 1; t >= 0; t--) {
    const hour = hours[t];
    if (!hour) continue;
    const importPrice = importPrices[t] ?? 0;
    const exportRev = exportRevenues[t] ?? 0;

    for (let s = 0; s < N; s++) {
      const currentSocKWh = minSoc + s * socStep;
      let bestCost = Number.POSITIVE_INFINITY;
      let bestChoice = choice[t]![s]!;

      // Pröva alla möjliga nästa SoC-nivåer
      for (let s2 = 0; s2 < N; s2++) {
        const nextSocKWh = minSoc + s2 * socStep;
        const deltaKWh = nextSocKWh - currentSocKWh;

        // Beräkna AC-flöden från det önskade SoC-bytet
        let chargeAC = 0;
        let dischargeAC = 0;
        if (deltaKWh > 0.0001) {
          chargeAC = deltaKWh / chargeEff;
          if (chargeAC > battery.maxPowerKW + 0.0001) continue; // omöjligt
        } else if (deltaKWh < -0.0001) {
          dischargeAC = -deltaKWh * dischargeEff;
          if (dischargeAC > battery.maxPowerKW + 0.0001) continue;
        }

        // Hur ska timmens kostnad beräknas givet detta beslut?
        //
        // Solflödet: batteriet kan ta från solöverskottet före export
        // går till nätet. Vi prioriterar alltid solladdning över nätladdning
        // eftersom solladdning är "gratis" (vi förlorar bara exportintäkt).
        const chargeFromSolar = Math.min(chargeAC, hour.exportKWh);
        const chargeFromGrid = chargeAC - chargeFromSolar;

        // Effektiva nätflöden efter batteriets aktivitet
        const netImport = Math.max(
          0,
          hour.importKWh - dischargeAC + chargeFromGrid,
        );
        const netExport = Math.max(0, hour.exportKWh - chargeFromSolar);

        // Hård constraint: om importCap är satt, hoppa över transitioner
        // som överskrider månadens tillåtna max-import. Förhindrar att DP
        // skapar nya peaks via aggressiv grid-laddning.
        const cap = importCap?.[t];
        if (cap !== undefined && netImport > cap + 0.01) {
          continue;
        }

        // Kostnaden för timmen
        const hourCost = netImport * importPrice - netExport * exportRev;

        const totalCost = hourCost + value[t + 1]![s2]!;
        if (totalCost < bestCost) {
          bestCost = totalCost;
          bestChoice = {
            nextS: s2,
            chargeAC,
            dischargeAC,
            chargeFromSolar,
            chargeFromGrid,
          };
        }
      }

      value[t]![s] = bestCost;
      choice[t]![s] = bestChoice;
    }
  }

  // Optimal startSoC: börja på minSoc (s=0) eftersom batteriet är tomt
  let currentS = 0;
  // value[0][0] är DP-internal optimum (kan inkludera skuggpriser); vi
  // räknar om till RAW-kostnader vid trajektorie-återskapning nedan.

  // Återskapa optimal trajektoria. Vi tracka två kostnader: en med
  // skuggpriser (för internal DP-bokföring) och en med RAW priser (för
  // verklig rapportering). Det är RAW vi exponerar i resultatet.
  const result: ResultHour[] = [];
  let cumulativeDischarged = 0;
  let rawOptimalCost = 0;
  for (let t = 0; t < T; t++) {
    const hour = hours[t]!;
    const ch = choice[t]![currentS]!;
    const nextSocKWh = minSoc + ch.nextS * socStep;

    const netImport = Math.max(
      0,
      hour.importKWh - ch.dischargeAC + ch.chargeFromGrid,
    );
    const netExport = Math.max(0, hour.exportKWh - ch.chargeFromSolar);
    const rawImportPrice = rawImportPrices[t]!;
    const exportRev = exportRevenues[t]!;
    const rawHourCost = netImport * rawImportPrice - netExport * exportRev;
    rawOptimalCost += rawHourCost;

    result.push({
      from: hour.from,
      importKWh: netImport,
      exportKWh: netExport,
      batteryChargeFromSolarKWh: ch.chargeFromSolar,
      batteryChargeFromGridKWh: ch.chargeFromGrid,
      batteryDischargeKWh: ch.dischargeAC,
      socKWh: nextSocKWh,
      netCostSek: rawHourCost,
    });

    cumulativeDischarged += ch.dischargeAC;
    currentS = ch.nextS;
  }

  // Räkna baseline-kostnad för jämförelse — med RAW-priser (skuggpriser
  // syns inte i monetär rapportering, de är bara internal DP-mekanism)
  let rawBaselineCost = 0;
  for (let t = 0; t < T; t++) {
    const hour = hours[t]!;
    rawBaselineCost +=
      hour.importKWh * rawImportPrices[t]! -
      hour.exportKWh * exportRevenues[t]!;
  }

  // Annualisering
  const hourlyFactor = 8760 / T;
  const annualOptimalCost = rawOptimalCost * hourlyFactor;
  const annualBaselineCost = rawBaselineCost * hourlyFactor;
  const annualSavings = annualBaselineCost - annualOptimalCost;

  const fullCycles =
    (cumulativeDischarged * hourlyFactor) /
    Math.max(battery.capacityKWh * (1 - battery.minSocFraction), 1);

  return {
    annualCostSek: annualOptimalCost,
    annualSavingsSek: annualSavings,
    hours: result,
    socLevels: N,
    fullCycles,
  };
}

/**
 * Peak-aware DP-optimering via iterativ shadow pricing.
 *
 * Effekttariff är ett "topp-N timmar"-problem: månadens kostnad är
 * pricePerKWPerMonth * avg(top-N största import-timmarna). Att lägga in
 * detta direkt i DP kräver en extra state-dimension (månadens hittills-
 * högsta) vilket ger O(T*N²*P²) komplexitet — för långsamt.
 *
 * Klassisk lösning: shadow pricing. Vi kör vanlig DP iterativt. Efter
 * varje körning identifierar vi månadens topp-N timmar och applicerar
 * skuggkostnader på dem i nästa iteration. Konvergerar typiskt på 2-4
 * iterationer.
 *
 * Skuggkostnaden härleds från effekttariff-matematiken: en topp-N timme
 * med 1 kW import bidrar till månadens kostnad med pricePerKWPerMonth / N
 * (eftersom vi medelvärdes över N timmar). Skuggpriset per kWh = exakt
 * denna marginalkostnad. DP "ser" då att topp-timmar är dyrare och kapar
 * dem om det är billigare än alternativet.
 */
export function optimizeWithDPPeakAware(
  hours: HourPoint[],
  battery: BatteryConfig,
  tax: TaxConfig,
  supplier: SupplierConfig,
  grid: GridFeeConfig,
  socLevels = 31,
  maxIterations = 12,
): DPResult {
  const pt = grid.powerTariff;
  if (!pt || (pt.activateFromYear ?? 0) > 0) {
    // Ingen aktiv effekttariff — kör vanlig DP
    return optimizeWithDP(hours, battery, tax, supplier, grid, socLevels);
  }
  // Lokal const efter narrowing — TypeScript glömmer narrowing inuti closures
  const powerTariff = pt;

  const T = hours.length;
  const N = powerTariff.topHoursAveraged;

  // Bygg månadsindex för varje timme
  const monthIndex: string[] = hours.map((h) => h.from.slice(0, 7));
  const uniqueMonths = Array.from(new Set(monthIndex));

  // Säsongs-multiplikator per månad
  function seasonMultiplier(monthKey: string): number {
    if (!powerTariff.seasonalDifferentiation) return 1;
    const month = parseInt(monthKey.slice(5, 7), 10);
    return powerTariff.seasonalDifferentiation.winterMonths.includes(month)
      ? powerTariff.seasonalDifferentiation.winterMultiplier
      : 1;
  }

  // VAT-faktor: marginalkostnaden på import är inkl moms,
  // så effekttariff-skuggpriset ska också vara inkl moms för konsistens.
  const vatFactor = 1 + tax.vatRate;

  // Beräkna baseline-cap per månad. Effekttariffen baseras på top-N medel,
  // inte topp-1. För att garantera att batteri-top-N medel <= baseline-top-N
  // medel räcker det att cap = N:te högsta baseline-importen för månaden.
  // Det följer matematiskt: om alla batteri-imports <= baselineSorted[N-1]
  // så är batteri-top-N alla <= baselineSorted[N-1] <= baselineSorted[i] för
  // alla i<N, alltså batteri-top-N medel <= baseline-top-N medel.
  const monthCap = new Map<string, number>();
  for (const monthKey of uniqueMonths) {
    const monthImports: number[] = [];
    for (let t = 0; t < T; t++) {
      if (monthIndex[t] === monthKey) monthImports.push(hours[t]!.importKWh);
    }
    monthImports.sort((a, b) => b - a);
    // N:te högsta = index N-1 (efter sortering fallande)
    const nthHighest = monthImports[Math.min(N - 1, monthImports.length - 1)]!;
    monthCap.set(monthKey, nthHighest);
  }
  const importCap = hours.map(
    (_, t) => monthCap.get(monthIndex[t]!)!,
  );

  let peakPenalties: number[] = new Array(T).fill(0);
  let result: DPResult | null = null;
  let previousPeakHours: Set<number> = new Set();

  for (let iter = 0; iter < maxIterations; iter++) {
    result = optimizeWithDP(
      hours,
      battery,
      tax,
      supplier,
      grid,
      socLevels,
      peakPenalties,
      importCap,
    );

    // Identifiera nya topp-N timmar per månad utifrån DP-resultatets import
    const newPeakHours = new Set<number>();
    const newPenalties = new Array(T).fill(0);

    for (const monthKey of uniqueMonths) {
      const monthHourIndices: number[] = [];
      for (let t = 0; t < T; t++) {
        if (monthIndex[t] === monthKey) monthHourIndices.push(t);
      }
      const sortedByImport = [...monthHourIndices].sort(
        (a, b) => result!.hours[b]!.importKWh - result!.hours[a]!.importKWh,
      );
      const topN = sortedByImport.slice(0, N);

      const shadowPrice =
        (powerTariff.pricePerKWPerMonth *
          seasonMultiplier(monthKey) *
          vatFactor) /
        N;

      for (const t of topN) {
        newPeakHours.add(t);
        newPenalties[t] = shadowPrice;
      }
    }

    // Konvergenskoll: har peak-timmarna stabiliserats?
    const converged =
      iter > 0 &&
      newPeakHours.size === previousPeakHours.size &&
      [...newPeakHours].every((t) => previousPeakHours.has(t));

    peakPenalties = newPenalties;
    previousPeakHours = newPeakHours;

    if (converged) break;
  }

  return result!;
}
